/**
 * Workspace Service
 *
 * API service module for workspace CRUD, member management, and settings.
 *
 * Note: Individual endpoint implementations will be added in subsequent tasks.
 */

import { apiClient, extractData } from './api';
import type { Workspace, WorkspaceMember, WorkspaceMemberRole } from '@mellify/shared';

// ─── Response Types ─────────────────────────────────────────────────────────

/**
 * Workspace list item with aggregated counts from the API.
 */
export interface WorkspaceListItem extends Workspace {
  memberCount: number;
  channelCount: number;
}

// ─── Request Types ──────────────────────────────────────────────────────────

export interface CreateWorkspaceRequest {
  name: string;
  slug?: string;
  description?: string;
  industry?: string;
  timezone?: string;
  defaultLanguage?: string;
  website?: string;
}

export interface UpdateWorkspaceRequest {
  name?: string;
  description?: string;
  industry?: string;
  timezone?: string;
  defaultLanguage?: string;
  website?: string;
}

export interface InviteMemberRequest {
  email: string;
  role: WorkspaceMemberRole;
}

export interface InviteMembersRequest {
  emails: string[];
  role: WorkspaceMemberRole;
  personalMessage?: string;
}

export interface InvitationResult {
  email: string;
  status: 'invited' | 'already_member' | 'already_invited' | 'error';
  message: string;
  memberId?: string;
}

export interface InviteMembersResponse {
  results: InvitationResult[];
  invitedCount: number;
  skippedCount: number;
}

export interface UpdateMemberRoleRequest {
  role: WorkspaceMemberRole;
}

export interface UpdateMemberRoleResponse {
  id: string;
  workspaceId: string;
  userId: string;
  role: string;
  status: string;
  updatedAt: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    avatarUrl: string | null;
  };
}

export interface SuspendMemberRequest {
  reason?: string;
}

export interface SuspendMemberResponse {
  id: string;
  workspaceId: string;
  userId: string;
  role: string;
  status: string;
  updatedAt: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    avatarUrl: string | null;
  };
}

export interface RemoveMemberResponse {
  id: string;
  workspaceId: string;
  userId: string;
  removedAt: string;
}

export interface ResendInviteResponse {
  memberId: string;
  email: string;
  resentAt: string;
}

export interface TransferOwnershipRequest {
  newOwnerId: string;
  password: string;
}

export interface DeleteWorkspaceRequest {
  confirmationName: string;
  password: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ListWorkspacesParams {
  page?: number;
  limit?: number;
}

// ─── Enhanced Member Types ──────────────────────────────────────────────────

export interface MemberListItem {
  id: string;
  workspaceId: string;
  userId: string;
  role: string;
  status: string;
  permissions: unknown;
  invitedBy: string | null;
  invitedAt: string | null;
  acceptedAt: string | null;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    avatarUrl: string | null;
  };
}

export interface MemberListPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface MemberListSummary {
  totalMembers: number;
  roleDistribution: Record<string, number>;
  statusDistribution: Record<string, number>;
}

export interface ListMembersParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: string;
}

export interface ListMembersResponse {
  members: MemberListItem[];
  pagination: MemberListPagination;
  summary: MemberListSummary;
}

// ─── Workspace Service ──────────────────────────────────────────────────────

export const workspaceService = {
  /**
   * List all workspaces the current user belongs to,
   * enriched with member and channel counts.
   */
  list: (): Promise<WorkspaceListItem[]> =>
    extractData(apiClient.get<WorkspaceListItem[]>('/workspaces')),

  /**
   * Get a single workspace by ID.
   */
  getById: (workspaceId: string): Promise<Workspace> =>
    extractData(apiClient.get<Workspace>(`/workspaces/${workspaceId}`)),

  /**
   * Create a new workspace.
   */
  create: (data: CreateWorkspaceRequest): Promise<Workspace> =>
    extractData(apiClient.post<Workspace>('/workspaces', data)),

  /**
   * Update a workspace.
   */
  update: (workspaceId: string, data: UpdateWorkspaceRequest): Promise<Workspace> =>
    extractData(apiClient.patch<Workspace>(`/workspaces/${workspaceId}`, data)),

  /**
   * Update workspace settings (JSONB settings field).
   * Uses PUT /workspaces/:id/settings to merge into the settings JSON.
   */
  updateSettings: (workspaceId: string, settings: Record<string, unknown>): Promise<Workspace> =>
    extractData(apiClient.put<Workspace>(`/workspaces/${workspaceId}/settings`, { settings })),

  /**
   * Delete a workspace (soft delete with 30-day recovery period).
   */
  delete: (workspaceId: string): Promise<void> =>
    extractData(apiClient.delete(`/workspaces/${workspaceId}`)),

  /**
   * Delete a workspace with confirmation (soft delete with 30-day recovery period).
   * Requires workspace name confirmation and password.
   */
  deleteWorkspace: (workspaceId: string, data: DeleteWorkspaceRequest): Promise<void> =>
    extractData(
      apiClient.delete(`/workspaces/${workspaceId}`, { data })
    ),

  /**
   * Transfer workspace ownership to another member.
   * Requires password confirmation.
   */
  transferOwnership: (workspaceId: string, data: TransferOwnershipRequest): Promise<Workspace> =>
    extractData(
      apiClient.post<Workspace>(`/workspaces/${workspaceId}/transfer-ownership`, data)
    ),

  /**
   * Upload a workspace logo.
   */
  uploadLogo: (workspaceId: string, file: File): Promise<{ logoUrl: string }> => {
    const formData = new FormData();
    formData.append('logo', file);
    return extractData(
      apiClient.post<{ logoUrl: string }>(`/workspaces/${workspaceId}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    );
  },

  // ─── Member Management ──────────────────────────────────────────────────

  /**
   * List workspace members with pagination, search, and filtering.
   * Returns members array, pagination info, and role/status distribution summary.
   */
  listMembers: async (
    workspaceId: string,
    params?: ListMembersParams
  ): Promise<ListMembersResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.search) queryParams.set('search', params.search);
    if (params?.role) queryParams.set('role', params.role);
    if (params?.status) queryParams.set('status', params.status);
    if (params?.sortBy) queryParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.set('sortOrder', params.sortOrder);

    const queryString = queryParams.toString();
    const url = `/workspaces/${workspaceId}/members${queryString ? `?${queryString}` : ''}`;

    // The API returns { data: members[], meta: { pagination, summary } }
    const response = await apiClient.get(url);
    const body = response.data as {
      success: boolean;
      data: MemberListItem[];
      meta?: {
        pagination?: MemberListPagination;
        summary?: MemberListSummary;
      };
    };

    return {
      members: body.data || [],
      pagination: body.meta?.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPrevPage: false,
      },
      summary: body.meta?.summary || {
        totalMembers: 0,
        roleDistribution: {},
        statusDistribution: {},
      },
    };
  },

  /**
   * Invite members to the workspace with role assignment and optional message.
   * Supports multiple emails in a single request.
   */
  inviteMembers: async (
    workspaceId: string,
    data: InviteMembersRequest
  ): Promise<InviteMembersResponse> => {
    const response = await apiClient.post(
      `/workspaces/${workspaceId}/members/invite`,
      data
    );
    const body = response.data as {
      success: boolean;
      data: InviteMembersResponse;
    };
    return body.data;
  },

  /**
   * Invite a new member to the workspace (single email, legacy).
   */
  inviteMember: (workspaceId: string, data: InviteMemberRequest): Promise<WorkspaceMember> =>
    extractData(apiClient.post<WorkspaceMember>(`/workspaces/${workspaceId}/members`, data)),

  /**
   * Update a member's role.
   * Uses PUT /workspaces/:workspaceId/members/:memberId/role
   */
  updateMemberRole: (
    workspaceId: string,
    memberId: string,
    data: UpdateMemberRoleRequest
  ): Promise<UpdateMemberRoleResponse> =>
    extractData(
      apiClient.put<UpdateMemberRoleResponse>(
        `/workspaces/${workspaceId}/members/${memberId}/role`,
        data
      )
    ),

  /**
   * Suspend a workspace member.
   * Suspended members can't access workspace but retain data.
   * Uses PUT /workspaces/:workspaceId/members/:memberId/suspend
   */
  suspendMember: (
    workspaceId: string,
    memberId: string,
    data?: SuspendMemberRequest
  ): Promise<SuspendMemberResponse> =>
    extractData(
      apiClient.put<SuspendMemberResponse>(
        `/workspaces/${workspaceId}/members/${memberId}/suspend`,
        data || {}
      )
    ),

  /**
   * Unsuspend (reactivate) a suspended workspace member.
   * Uses PUT /workspaces/:workspaceId/members/:memberId/unsuspend
   */
  unsuspendMember: (
    workspaceId: string,
    memberId: string
  ): Promise<SuspendMemberResponse> =>
    extractData(
      apiClient.put<SuspendMemberResponse>(
        `/workspaces/${workspaceId}/members/${memberId}/unsuspend`
      )
    ),

  /**
   * Remove a member from the workspace.
   * Uses DELETE /workspaces/:workspaceId/members/:memberId
   */
  removeMember: (workspaceId: string, memberId: string): Promise<RemoveMemberResponse> =>
    extractData(
      apiClient.delete<RemoveMemberResponse>(
        `/workspaces/${workspaceId}/members/${memberId}`
      )
    ),

  /**
   * Resend an invitation email for a pending member.
   * Rate limited: max 3 resends per invitation.
   * Uses POST /workspaces/:workspaceId/members/:memberId/resend-invite
   */
  resendInvite: (
    workspaceId: string,
    memberId: string
  ): Promise<ResendInviteResponse> =>
    extractData(
      apiClient.post<ResendInviteResponse>(
        `/workspaces/${workspaceId}/members/${memberId}/resend-invite`
      )
    ),
};